/** Embed API auth context.
 *
 * The SPA authenticates workspace API calls with the HttpOnly `egpt_session`
 * cookie (BFF flow) via `apiFetch`. The rich embed has no cookie — it holds a
 * short-lived backend-mint userJWT in memory. When the embed mounts the
 * workspace pages (Skills, Knowledge, …) those pages call `useAuthedApi`, which
 * would otherwise use the cookie path and 401.
 *
 * This context carries the embed's API base + a token getter so `useAuthedApi`
 * can build its REST clients with a Bearer-injecting fetch instead. It is null
 * in the normal SPA (cookie mode, unchanged).
 */
import { createContext, useContext } from "react";

export interface EmbedApiAuth {
  /** Origin that fronts the API (no trailing slash), e.g. https://artie.example.com */
  apiUrl: string;
  /** Resolve the current end-user JWT (backend-mint). Called per request so a
   *  refreshed token is always picked up. */
  getToken: () => string | Promise<string>;
}

const EmbedApiContext = createContext<EmbedApiAuth | null>(null);

export const EmbedApiProvider = EmbedApiContext.Provider;

/** The embed API auth, or null in the cookie-authed SPA. */
export function useEmbedApiAuth(): EmbedApiAuth | null {
  return useContext(EmbedApiContext);
}

/** A fetch wrapper that injects `Authorization: Bearer <jwt>` from the embed
 *  token getter. No cookie — the embed is cross-origin to the API and relies on
 *  the bearer token. Resolves the token per call so refresh is transparent. */
export function makeBearerFetch(
  getToken: () => string | Promise<string>,
): typeof globalThis.fetch {
  return async (input, init) => {
    const token = await getToken();
    const headers = new Headers(init?.headers ?? {});
    if (token) headers.set("Authorization", `Bearer ${token}`);
    // credentials:'omit' — no cookie to send cross-origin; auth is the bearer.
    return fetch(input, { ...init, headers, credentials: "omit" });
  };
}
